import React, { useState, useEffect, useCallback } from 'react';
import {
    collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Stethoscope, Link2, Copy, Trash2, Plus } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { getFeatures } from '../utils/features';

// 🦷 치과 초대 관리 (기공소용) — features.clinicInvite 플래그 뒤에 숨김
// 기공소가 초대 링크를 만들어 거래 치과에 전달 → 치과가 링크로 가입하면 자동 거래처 연결
function ClinicInvites({ user }) {
    const targetId = user?.companyId || user?.uid;
    const labName = user?.businessName || user?.companyName || user?.name || '';

    const [featureOn, setFeatureOn] = useState(null); // null=로딩, true/false
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [clinicNameInput, setClinicNameInput] = useState('');
    const [creating, setCreating] = useState(false);
    const [copiedCode, setCopiedCode] = useState('');
    const [usedByNames, setUsedByNames] = useState({}); // uid → 치과명

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatureOn(f?.clinicInvite === true); });
        return () => { alive = false; };
    }, []);

    const fetchInvites = useCallback(async () => {
        if (!targetId) return;
        try {
            setLoading(true);
            // ⚠️ rules의 list 허용 범위가 넓으므로(로그인 사용자 전체) 반드시 labId where로만 조회한다
            const q = query(collection(db, 'clinicInvites'), where('labId', '==', targetId));
            const snap = await getDocs(q);
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (toMs(b.createdAt) - toMs(a.createdAt)));
            setInvites(rows);

            // 가입 완료된 초대 → 치과명 조회
            const names = {};
            for (const inv of rows) {
                if (inv.usedBy && !names[inv.usedBy]) {
                    try {
                        const uSnap = await getDoc(doc(db, 'users', inv.usedBy));
                        if (uSnap.exists()) {
                            const u = uSnap.data();
                            names[inv.usedBy] = u.businessName || u.name || '';
                        }
                    } catch (e) { /* 조회 실패 시 메모(clinicName)로 대체 */ }
                }
            }
            setUsedByNames(names);
        } catch (e) {
            console.error('초대 목록 조회 실패:', e);
        } finally {
            setLoading(false);
        }
    }, [targetId]);

    useEffect(() => { fetchInvites(); }, [fetchInvites]);

    const toMs = (t) => {
        if (!t) return 0;
        if (typeof t.toMillis === 'function') return t.toMillis();
        if (t instanceof Date) return t.getTime();
        return new Date(t).getTime() || 0;
    };

    const isExpired = (inv) => {
        const ms = toMs(inv.expiresAt);
        return ms > 0 && ms < Date.now();
    };

    const statusOf = (inv) => {
        if (inv.status === 'used') return { label: '가입완료', bg: '#dcfce7', color: '#15803d' };
        if (inv.status === 'expired' || isExpired(inv)) return { label: '만료', bg: '#f1f5f9', color: '#94a3b8' };
        return { label: '대기', bg: '#fef3c7', color: '#b45309' };
    };

    // 코드 생성: 영대문자+숫자 8자 (혼동 문자 제외 없이 단순 랜덤, 충돌 시 재시도)
    const generateCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
        return code;
    };

    const handleCreate = async () => {
        if (!targetId) return;
        setCreating(true);
        try {
            // 충돌 확인 (최대 5회 재시도)
            let code = generateCode();
            for (let i = 0; i < 5; i++) {
                const exist = await getDoc(doc(db, 'clinicInvites', code));
                if (!exist.exists()) break;
                code = generateCode();
            }
            const expiresAt = Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
            await setDoc(doc(db, 'clinicInvites', code), {
                labId: targetId,
                labName: labName,
                clinicName: clinicNameInput.trim(),
                status: 'active',
                createdAt: Timestamp.now(),
                expiresAt,
                usedBy: null,
                usedAt: null,
            });
            setClinicNameInput('');
            setShowCreate(false);
            await fetchInvites();
            // 생성 직후 바로 복사 유도
            handleCopy(code);
        } catch (e) {
            console.error('초대 생성 실패:', e);
            alert('초대 링크 생성에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setCreating(false);
        }
    };

    const inviteUrl = (code) => `https://dentalcluster.com/signup?invite=${code}`;

    const handleCopy = async (code) => {
        try {
            await navigator.clipboard.writeText(inviteUrl(code));
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(''), 2000);
        } catch (e) {
            window.prompt('아래 링크를 복사해주세요:', inviteUrl(code));
        }
    };

    const handleDelete = async (code) => {
        if (!window.confirm('이 초대 링크를 삭제할까요? 삭제하면 해당 링크로 가입할 수 없습니다.')) return;
        try {
            await deleteDoc(doc(db, 'clinicInvites', code));
            await fetchInvites();
        } catch (e) {
            console.error('초대 삭제 실패:', e);
            alert('삭제에 실패했습니다.');
        }
    };

    const fmtDate = (t) => {
        const ms = toMs(t);
        if (!ms) return '-';
        const d = new Date(ms);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    // 플래그 OFF: 아무것도 노출하지 않음 (준비 중 안내만)
    if (featureOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={Stethoscope} title="치과 초대" />
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                    아직 준비 중인 기능입니다.
                </div>
            </div>
        );
    }

    return (
        <div style={S.container}>
            <PageHeader icon={Stethoscope} title="치과 초대" />

            <div style={S.body}>
                {/* 안내 */}
                <div style={S.notice}>
                    초대 링크로 가입한 치과는 자동으로 거래처로 연결되며, 웹으로 의뢰서를 보낼 수 있습니다. 치과는 무료입니다.
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                        링크는 생성 후 14일간 유효하며, 만료되면 새로 만들어 다시 전달하시면 됩니다.
                    </div>
                </div>

                {/* 초대 만들기 */}
                {!showCreate ? (
                    <button onClick={() => setShowCreate(true)} style={S.createBtn}>
                        <Plus size={16} /> 치과 초대 링크 만들기
                    </button>
                ) : (
                    <div style={S.createBox}>
                        <label style={S.label}>치과명 (메모, 선택)</label>
                        <input
                            type="text"
                            value={clinicNameInput}
                            onChange={(e) => setClinicNameInput(e.target.value)}
                            placeholder="예: ○○치과 (어느 치과에 보낼 링크인지 메모)"
                            style={S.input}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button onClick={handleCreate} disabled={creating} style={{ ...S.primaryBtn, opacity: creating ? 0.6 : 1 }}>
                                {creating ? '생성 중...' : '링크 생성'}
                            </button>
                            <button onClick={() => { setShowCreate(false); setClinicNameInput(''); }} style={S.secondaryBtn}>
                                취소
                            </button>
                        </div>
                    </div>
                )}

                {/* 목록 */}
                {loading || featureOn === null ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>불러오는 중...</div>
                ) : invites.length === 0 ? (
                    <div style={S.empty}>
                        <Link2 size={28} color="#cbd5e1" />
                        <div style={{ marginTop: 10, fontSize: 14, color: '#94a3b8' }}>
                            아직 만든 초대 링크가 없습니다.<br />
                            [치과 초대 링크 만들기]로 첫 초대를 만들어 카톡·문자로 전달해보세요.
                        </div>
                    </div>
                ) : (
                    <div style={S.tableWrap}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>코드</th>
                                    <th style={S.th}>치과명</th>
                                    <th style={S.th}>상태</th>
                                    <th style={S.th}>생성일</th>
                                    <th style={S.th}>만료일</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invites.map(inv => {
                                    const st = statusOf(inv);
                                    const isActive = inv.status === 'active' && !isExpired(inv);
                                    const clinicLabel = inv.status === 'used'
                                        ? (usedByNames[inv.usedBy] || inv.clinicName || '-')
                                        : (inv.clinicName || '-');
                                    return (
                                        <tr key={inv.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}>{inv.id}</td>
                                            <td style={S.td}>
                                                {clinicLabel}
                                                {inv.status === 'used' && usedByNames[inv.usedBy] && (
                                                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>가입 치과</span>
                                                )}
                                            </td>
                                            <td style={S.td}>
                                                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: st.bg, color: st.color }}>
                                                    {st.label}
                                                </span>
                                            </td>
                                            <td style={S.td}>{fmtDate(inv.createdAt)}</td>
                                            <td style={S.td}>{fmtDate(inv.expiresAt)}</td>
                                            <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isActive && (
                                                    <>
                                                        <button onClick={() => handleCopy(inv.id)} style={S.rowBtn}>
                                                            <Copy size={13} /> {copiedCode === inv.id ? '복사됨!' : '링크 복사'}
                                                        </button>
                                                        <button onClick={() => handleDelete(inv.id)} style={{ ...S.rowBtn, color: '#ef4444', borderColor: '#fecaca', marginLeft: 6 }}>
                                                            <Trash2 size={13} /> 삭제
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const S = {
    container: { maxWidth: 960, margin: '0 auto' },
    body: { padding: 24 },
    notice: {
        padding: '14px 16px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe',
        borderRadius: 10, fontSize: 13, color: '#3730a3', lineHeight: 1.7, marginBottom: 16,
    },
    createBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '11px 18px', backgroundColor: '#6366f1', color: '#fff',
        border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        marginBottom: 20,
    },
    createBox: {
        padding: 16, backgroundColor: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, marginBottom: 20,
    },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 },
    input: {
        width: '100%', boxSizing: 'border-box', padding: '10px 14px',
        border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14,
    },
    primaryBtn: {
        padding: '9px 18px', backgroundColor: '#6366f1', color: '#fff',
        border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    secondaryBtn: {
        padding: '9px 18px', backgroundColor: '#fff', color: '#64748b',
        border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    empty: {
        padding: '48px 20px', textAlign: 'center', backgroundColor: '#fff',
        border: '1px dashed #e2e8f0', borderRadius: 12, lineHeight: 1.7,
    },
    tableWrap: {
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        overflowX: 'auto',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: {
        padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700,
        color: '#94a3b8', backgroundColor: '#f8fafc', whiteSpace: 'nowrap',
    },
    td: { padding: '12px 14px', color: '#334155', verticalAlign: 'middle' },
    rowBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '6px 10px', backgroundColor: '#fff', color: '#6366f1',
        border: '1px solid #c7d2fe', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    },
};

export default ClinicInvites;
